// Browser-runtime shutdown boundary for a signed CLIENT_CLOCK_UNSAFE event.
//
// This module is installed by the support bundle before Flutter boots. That
// ordering is what lets it observe every dedicated Worker created by Dart
// packages (including dart_webrtc's singleton E2EE worker) without importing
// package-private implementation types.

const TERMINAL_MESSAGE = Object.freeze({ type: 'ski2/clientClockUnsafe' });
export const CLOCK_UNSAFE_LATCH_KEY =
  'flutter.security.client_clock_unsafe.v1';

function securityError(scope) {
  const DomException = scope.DOMException ?? globalThis.DOMException;
  return typeof DomException === 'function'
    ? new DomException('Client clock is unsafe', 'SecurityError')
    : new Error('Client clock is unsafe');
}

export function installClockUnsafeShutdown(scope, setLoggedInVerified) {
  const state = {
    terminal: false,
    workers: new Set(),
    shutdown: null,
  };

  const NativeWorker = scope.Worker;
  if (typeof NativeWorker === 'function') {
    let GuardedWorker;
    GuardedWorker = new Proxy(NativeWorker, {
      construct(target, args, newTarget) {
        if (state.terminal) throw securityError(scope);
        const actualNewTarget = newTarget === GuardedWorker ? target : newTarget;
        const worker = Reflect.construct(target, args, actualNewTarget);
        state.workers.add(worker);
        return worker;
      },
    });
    Object.defineProperty(scope, 'Worker', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: GuardedWorker,
    });
  }

  scope.ski2ClockUnsafeShutdown = () => {
    if (state.shutdown) return state.shutdown;
    state.terminal = true;

    // Defense in depth for Web: SharedPreferences uses this exact localStorage
    // key/value encoding. Dart releases the terminal event only after its
    // acknowledged write, and this synchronous mirror ensures the bit is set
    // before any worker can observe another queued frame.
    try { scope.localStorage?.setItem(CLOCK_UNSAFE_LATCH_KEY, 'true'); } catch (_) {}

    // This loop runs synchronously before the returned Promise exists. The
    // cryptographic worker therefore cannot process another queued frame once
    // Dart invokes the bridge, even if IDB or service-worker APIs are slow.
    for (const worker of state.workers) {
      try { worker.terminate(); } catch (_) {}
    }
    state.workers.clear();

    const serviceWorkers = (async () => {
      const container = scope.navigator?.serviceWorker;
      if (!container?.getRegistrations) return;
      const registrations = await container.getRegistrations();
      for (const registration of registrations) {
        for (const worker of [
          registration.installing,
          registration.waiting,
          registration.active,
        ]) {
          try { worker?.postMessage(TERMINAL_MESSAGE); } catch (_) {}
        }
      }
      await Promise.allSettled(registrations.map((item) => item.unregister()));
    })();

    // Never reject across the Dart JS boundary. The dedicated workers are
    // already stopped; each browser persistence/registration cleanup is
    // independent and best-effort so one unavailable API cannot skip another.
    state.shutdown = Promise.allSettled([
      Promise.resolve().then(() => setLoggedInVerified(false)),
      serviceWorkers,
    ]).then(() => undefined);
    return state.shutdown;
  };

  return state;
}
